"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AuthHeader from "../../_components/header";
import { useAuthContext } from "../../_components/authProvider";
import { FormEvent, useState } from "react";
import { LoaderCircle } from "lucide-react";
import { signIn } from "next-auth/react";
import Messages from "../../_components/messages";
import { onboardingPath } from "@/features/auth/utils";
import {
    checkIfUserWithEmailExists,
    createUserWithEmailAndPassword,
} from "@/features/auth/lib/actions";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";

export default function SignupForm() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    const { loading, setLoading, setErrorMessage } = useAuthContext();
    const { locale } = useLocale();
    const withLocale = (path: string) => `/${locale}${path}`;

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) {
            setErrorMessage({
                keyName: "auth-card.errors.email.required",
                defaultValue: t("auth-card.errors.email.required"),
            });
            return;
        }
        if (!emailRegex.test(email)) {
            setErrorMessage({
                keyName: "auth-card.errors.email.invalid",
                defaultValue: t("auth-card.errors.email.invalid"),
            });
            return;
        }
        if (!password) {
            setErrorMessage({
                keyName: "auth-card.errors.password.required",
                defaultValue: t("auth-card.errors.password.required"),
            });
            return;
        }
        if (password.length < 6) {
            setErrorMessage({
                keyName: "auth-card.errors.password.invalid",
                defaultValue: t("auth-card.errors.password.invalid"),
            });
            return;
        }

        setErrorMessage(null);
        setLoading(true);

        try {
            const emailExists = await checkIfUserWithEmailExists(email);

            if (emailExists) {
                setErrorMessage({
                    keyName: "signup.errors.email-already-exists",
                    defaultValue: t("signup.errors.email-already-exists"),
                });
                setLoading(false);
                return;
            }

            const createdUser = await createUserWithEmailAndPassword(
                email,
                password
            );
            if (!createdUser) {
                setErrorMessage({
                    keyName: "signup.errors.failed-to-create-user",
                    defaultValue: t("signup.errors.failed-to-create-user"),
                });
                setLoading(false);
                return;
            }

            await signIn("credentials", {
                email,
                password,
                redirectTo: withLocale(onboardingPath),
            });
        } catch (err) {
            setErrorMessage({
                keyName: "auth.errors.sign-in.failed",
                defaultValue: t("auth.errors.sign-in.failed"),
            });
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="p-6 md:p-8">
            <div className="flex flex-col gap-6">
                <AuthHeader
                    title={{
                        keyName: "signup.title",
                        defaultValue: t("signup.title"),
                    }}
                    description={{
                        keyName: "signup.description",
                        defaultValue: t("signup.description"),
                    }}
                />

                <div className="grid gap-2">
                    <Label htmlFor="email">
                        {t("signup.email", undefined, undefined)}
                    </Label>
                    <Input
                        disabled={loading}
                        id="email"
                        type="email"
                        placeholder="name@example.com"
                        value={email}
                        onChange={(e) => {
                            setErrorMessage(null);
                            setEmail(e.target.value.toLowerCase());
                        }}
                        required
                    />
                </div>
                <div className="grid gap-2">
                    <div className="flex items-center">
                        <Label htmlFor="password">
                            {t("signup.password", undefined, undefined)}
                        </Label>
                    </div>
                    <Input
                        disabled={loading}
                        id="password"
                        type="password"
                        required
                        value={password}
                        onChange={(e) => {
                            setErrorMessage(null);
                            setPassword(e.target.value);
                        }}
                    />
                </div>

                <Messages />

                <div className="flex flex-col w-full mt-2 gap-4">
                    <Button disabled={loading} type="submit" className="w-full">
                        {loading ? (
                            <LoaderCircle className="size-4 animate-spin" />
                        ) : (
                            t("signup.signup-btn", undefined, undefined)
                        )}
                    </Button>
                </div>

                <div className="text-center text-sm">
                    {t("signup.have-account")}{" "}
                    <a
                        href={withLocale("/auth/login")}
                        className="underline underline-offset-4"
                    >
                        {t("signup.login-link")}
                    </a>
                </div>
            </div>
        </form>
    );
}
